import "core-js/modules/es.object.from-entries.js";
import "core-js/modules/es.array.sort.js";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
const _excluded = ["width", "height", "priority", "layout", "aspectRatio"],
  _excluded2 = ["src", "cdn", "transformer", "background", "layout", "objectFit", "breakpoints", "width", "height", "aspectRatio", "unstyled"],
  _excluded3 = ["media", "type"],
  _excluded4 = ["src", "cdn", "transformer", "layout", "breakpoints", "width", "height", "aspectRatio", "sizes", "loading", "decoding"];
var _import$meta$env;
import "core-js/modules/es.object.from-entries.js";
import "core-js/modules/es.array.sort.js";
// src/core.ts
import { getCanonicalCdnForUrl, getTransformer } from "unpic";

// src/mediaquery.ts
var RE_MEDIA_QUERY = /^(?:(only|not)?\s*([_a-z][_a-z0-9-]*)|(\([^)]+\)))(?:\s*and\s*(.*))?$/i;
var RE_MQ_EXPRESSION = /^\(\s*([_a-z-][_a-z0-9-]*)\s*(?::\s*([^)]+))?\s*\)$/;
var RE_MQ_FEATURE = /^(?:(min|max)-)?(.+)/;
function parse(mediaQuery) {
  return mediaQuery.split(",").map(function (query) {
    query = query.trim();
    const captures = query.match(RE_MEDIA_QUERY);
    if (!captures) {
      throw new SyntaxError('Invalid CSS media query: "' + query + '"');
    }
    const modifier = captures[1];
    const type = captures[2];
    const parsed = {
      inverse: !!modifier && modifier.toLowerCase() === "not",
      type: type ? type.toLowerCase() : "all",
      expressions: []
    };
    const expressions = ((captures[3] || "") + (captures[4] || "")).trim();
    if (!expressions) {
      return parsed;
    }
    const expressionList = expressions.match(/\([^)]+\)/g);
    if (!expressionList) {
      throw new SyntaxError('Invalid CSS media query: "' + query + '"');
    }
    parsed.expressions = expressionList.map(function (expression) {
      var _feature$, _feature$2;
      const captures2 = expression.match(RE_MQ_EXPRESSION);
      if (!captures2) {
        throw new SyntaxError('Invalid CSS media query: "' + query + '"');
      }
      const feature = captures2[1].toLowerCase().match(RE_MQ_FEATURE);
      return {
        modifier: (_feature$ = feature === null || feature === void 0 ? void 0 : feature[1]) !== null && _feature$ !== void 0 ? _feature$ : "",
        feature: (_feature$2 = feature === null || feature === void 0 ? void 0 : feature[2]) !== null && _feature$2 !== void 0 ? _feature$2 : "",
        value: captures2[2]
      };
    });
    return parsed;
  });
}

// src/core.ts
var DEBUG = (_import$meta$env = import.meta.env) === null || _import$meta$env === void 0 ? void 0 : _import$meta$env.DEV;
function logError() {
  if (DEBUG) {
    var _console;
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    (_console = console).error.apply(_console, ["[unpic]"].concat(args));
  }
}
var getSizes = (width, layout) => {
  if (!width || !layout) {
    return void 0;
  }
  switch (layout) {
    case "constrained":
      return "(min-width: " + width + "px) " + width + "px, 100vw";
    case "fixed":
      return width + "px";
    case "fullWidth":
      return "100vw";
    default:
      return void 0;
  }
};
var pixelate = value => value || value === 0 ? value + "px" : void 0;
var getStyle = _ref => {
  let {
    width,
    height,
    aspectRatio,
    layout,
    objectFit = "cover",
    background
  } = _ref;
  const styleEntries = [["object-fit", objectFit]];
  if (background !== null && background !== void 0 && background.startsWith("https:") || background !== null && background !== void 0 && background.startsWith("http:") || background !== null && background !== void 0 && background.startsWith("data:")) {
    styleEntries.push(["background-image", "url(" + background + ")"]);
    styleEntries.push(["background-size", "cover"]);
    styleEntries.push(["background-repeat", "no-repeat"]);
  } else {
    styleEntries.push(["background", background]);
  }
  if (layout === "fixed") {
    styleEntries.push(["width", pixelate(width)]);
    styleEntries.push(["height", pixelate(height)]);
  }
  if (layout === "constrained") {
    styleEntries.push(["max-width", pixelate(width)]);
    styleEntries.push(["max-height", pixelate(height)]);
    styleEntries.push(["aspect-ratio", aspectRatio ? "" + aspectRatio : void 0]);
    styleEntries.push(["width", "100%"]);
  }
  if (layout === "fullWidth") {
    styleEntries.push(["width", "100%"]);
    styleEntries.push(["aspect-ratio", aspectRatio ? "" + aspectRatio : void 0]);
    styleEntries.push(["height", pixelate(height)]);
  }
  return Object.fromEntries(styleEntries.filter(_ref2 => {
    let [, value] = _ref2;
    return value;
  }));
};
var DEFAULT_RESOLUTIONS = [6016,
// 6K
5120,
// 5K
4480,
// 4.5K
3840,
// 4K
3200,
// QHD+
2560,
// WQXGA
2048,
// QXGA
1920,
// 1080p
1668,
// Various iPads
1280,
// 720p
1080,
// iPhone 6-8 Plus
960,
// older horizontal phones
828,
// iPhone XR/11
750,
// iPhone 6-8
640
// older and lower-end phones
];
var LOW_RES_WIDTH = 24;
var getBreakpoints = _ref3 => {
  let {
    width,
    layout,
    resolutions = DEFAULT_RESOLUTIONS
  } = _ref3;
  if (layout === "fullWidth") {
    return resolutions;
  }
  if (!width) {
    return [];
  }
  const doubleWidth = width * 2;
  if (layout === "fixed") {
    return [width, doubleWidth];
  }
  if (layout === "constrained") {
    return [
    // Always include the image at 1x and 2x the specified width
    width, doubleWidth].concat(_toConsumableArray(resolutions.filter(w => w < doubleWidth)));
  }
  return [];
};
var getSrcSetEntries = _ref4 => {
  let {
    src,
    width,
    layout = "constrained",
    height,
    aspectRatio,
    breakpoints,
    cdn,
    transformer,
    format
  } = _ref4;
  const canonical = getCanonicalCdnForUrl(src, cdn);
  if (canonical && !transformer) {
    transformer = getTransformer(canonical.cdn);
  }
  if (!transformer) {
    return [];
  }
  breakpoints || (breakpoints = getBreakpoints({
    width,
    layout
  }));
  return breakpoints.sort((a, b) => a - b).map(bp => {
    let transformedHeight;
    if (height && aspectRatio) {
      transformedHeight = Math.round(bp / aspectRatio);
    }
    return {
      url: canonical ? canonical.url : src,
      width: bp,
      height: transformedHeight,
      format
    };
  });
};
var getSrcSet = options => {
  let {
    src,
    cdn,
    transformer
  } = options;
  const canonical = getCanonicalCdnForUrl(src, cdn);
  if (canonical && !transformer) {
    transformer = getTransformer(canonical.cdn);
  }
  if (!transformer) {
    return "";
  }
  return getSrcSetEntries(Object.assign({}, options, {
    transformer
  })).map(transform => {
    const url = transformer(transform);
    return (url === null || url === void 0 ? void 0 : url.toString()) + " " + transform.width + "w";
  }).join(",\n");
};
function transformSharedProps(_ref5) {
  let {
      width,
      height,
      priority,
      layout = "constrained",
      aspectRatio
    } = _ref5,
    props = _objectWithoutPropertiesLoose(_ref5, _excluded);
  width = width && Number(width) || void 0;
  height = height && Number(height) || void 0;
  if (priority) {
    props.loading || (props.loading = "eager");
    props.fetchpriority || (props.fetchpriority = "high");
  } else {
    props.loading || (props.loading = "lazy");
    props.decoding || (props.decoding = "async");
  }
  if (props.alt === "") {
    props.role || (props.role = "presentation");
  }
  if (aspectRatio) {
    if (width) {
      if (height) {
        logError("Ignoring aspectRatio because width and height are both set");
      } else {
        height = Math.round(width / aspectRatio);
      }
    } else if (height) {
      width = Math.round(height * aspectRatio);
    } else if (layout !== "fullWidth") {
      logError("When aspectRatio is set, either width or height must also be set");
    }
  } else if (width && height) {
    aspectRatio = width / height;
  } else if (layout !== "fullWidth") {
    logError("Either aspectRatio or both width and height must be set");
  }
  return Object.assign({
    width,
    height,
    aspectRatio,
    layout
  }, props);
}
function transformProps(props) {
  var _url;
  let _transformSharedProps = transformSharedProps(props),
    {
      src,
      cdn,
      transformer,
      background,
      layout,
      objectFit,
      breakpoints,
      width,
      height,
      aspectRatio,
      unstyled
    } = _transformSharedProps,
    transformedProps = _objectWithoutPropertiesLoose(_transformSharedProps, _excluded2);
  const canonical = src ? getCanonicalCdnForUrl(src, cdn) : void 0;
  let url = src;
  if (canonical) {
    url = canonical.url;
    transformer || (transformer = getTransformer(canonical.cdn));
  }
  if (transformer && background === "auto") {
    const lowResHeight = aspectRatio ? Math.round(LOW_RES_WIDTH / aspectRatio) : void 0;
    const lowResImage = transformer({
      url,
      width: LOW_RES_WIDTH,
      height: lowResHeight
    });
    if (lowResImage) {
      background = lowResImage.toString();
    }
  }
  const styleProps = {
    width,
    height,
    aspectRatio,
    layout,
    objectFit,
    background
  };
  transformedProps.sizes || (transformedProps.sizes = getSizes(width, layout));
  if (!unstyled) {
    transformedProps.style = Object.assign({}, getStyle(styleProps), transformedProps.style);
  }
  if (transformer) {
    transformedProps.srcset = getSrcSet({
      src: url,
      width,
      height,
      aspectRatio,
      layout,
      breakpoints,
      transformer,
      cdn
    });
    const transformed = transformer({
      url,
      width,
      height
    });
    if (transformed) {
      url = transformed;
    }
    if (layout === "fullWidth" || layout === "constrained") {
      width = void 0;
      height = void 0;
    }
  }
  if (!url) {
    logError("No URL provided for image");
  }
  return Object.assign({}, transformedProps, {
    src: (_url = url) === null || _url === void 0 ? void 0 : _url.toString(),
    width,
    height
  });
}
function normalizeImageType(type) {
  if (!type) {
    return {};
  }
  if (type.startsWith("image/")) {
    return {
      format: type.slice(6),
      mimeType: type
    };
  }
  return {
    format: type,
    mimeType: "image/" + (type === "jpg" ? "jpeg" : type)
  };
}
function transformSourceProps(_ref6) {
  let {
      media,
      type
    } = _ref6,
    props = _objectWithoutPropertiesLoose(_ref6, _excluded3);
  let _transformSharedProps2 = transformSharedProps(props),
    {
      src,
      cdn,
      transformer,
      layout,
      breakpoints,
      width,
      height,
      aspectRatio,
      sizes
    } = _transformSharedProps2,
    rest = _objectWithoutPropertiesLoose(_transformSharedProps2, _excluded4);
  const canonical = src ? getCanonicalCdnForUrl(src, cdn) : void 0;
  let url = src;
  if (canonical) {
    url = canonical.url;
    transformer || (transformer = getTransformer(canonical.cdn));
  }
  if (!transformer) {
    return {};
  }
  const {
    format,
    mimeType
  } = normalizeImageType(type);
  sizes || (sizes = getSizes(width, layout));
  const srcset = getSrcSet({
    src: url,
    width,
    height,
    aspectRatio,
    layout,
    breakpoints,
    transformer,
    cdn,
    format
  });
  const transformed = transformer({
    url,
    width,
    height
  });
  if (transformed) {
    url = transformed;
  }
  const returnObject = Object.assign({}, rest, {
    sizes,
    srcset
  });
  if (media) {
    if (DEBUG) {
      try {
        parse(media);
        returnObject.media = media;
      } catch (e) {
        logError(e.message);
      }
    } else {
      returnObject.media = media;
    }
  }
  if (mimeType) {
    returnObject.type = mimeType;
  }
  return returnObject;
}
function inferImageDimensions(props, imageData) {
  const aspectRatio = props.aspectRatio || imageData.width / imageData.height;
  let {
    width,
    height
  } = props;
  if (!width) {
    if (height) {
      width = height * aspectRatio;
    } else {
      width = imageData.width;
    }
  }
  if (!height) {
    if (width) {
      height = width / aspectRatio;
    } else {
      height = imageData.height;
    }
  }
  return {
    width,
    height
  };
}
export { DEFAULT_RESOLUTIONS, getBreakpoints, getSizes, getSrcSet, getSrcSetEntries, getStyle, inferImageDimensions, logError, normalizeImageType, transformProps, transformSharedProps, transformSourceProps };